import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  Unarchive as UnarchiveIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  ContactMail as ContactMailIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import { formatDate } from '../utils/dateUtils';
import PageHeader from '../components/common/PageHeader';

const Archive = () => {
  const { getToken } = useAuth();
  const [tabValue, setTabValue] = useState(() => {
    const savedTab = localStorage.getItem('archiveActiveTab');
    return savedTab ? parseInt(savedTab, 10) : 0;
  });
  const [archivedOffers, setArchivedOffers] = useState([]);
  const [archivedOrganizations, setArchivedOrganizations] = useState([]);
  const [archivedAdOrders, setArchivedAdOrders] = useState([]);
  const [archivedAdEnquiries, setArchivedAdEnquiries] = useState([]);
  const [archivedStationeryOrders, setArchivedStationeryOrders] = useState([]);
  const [archivedStationeryEnquiries, setArchivedStationeryEnquiries] = useState([]);
  const [archivedSchoolEnquiries, setArchivedSchoolEnquiries] = useState([]);
  const [schoolEnquiriesLoading, setSchoolEnquiriesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [unarchiveDialog, setUnarchiveDialog] = useState({ open: false, item: null, type: null });
  
  // Store counts for tab labels
  const [counts, setCounts] = useState({
    offers: 0,
    organizations: 0,
    adOrders: 0,
    adEnquiries: 0,
    stationeryOrders: 0,
    stationeryEnquiries: 0,
    schoolEnquiries: 0
  });

  // Fetch counts for all tabs (lightweight)
  const fetchCounts = useCallback(async () => {
    try {
      const token = await getToken();
      
      // Fetch counts in parallel
      const [offersRes, orgsRes, ordersRes, stationeryEnquiriesRes] = await Promise.all([
        fetch(`${API_URL}/offers?archived=true&limit=1`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/organizations?archived=true&limit=1`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/orders/archived?limit=1`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/stationery-enquiries?status=converted&limit=1`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const newCounts = { ...counts };

      // Parse offers count
      if (offersRes.ok) {
        const offersData = await offersRes.json();
        newCounts.offers = offersData.pagination?.total || offersData.total || 0;
      }

      // Parse organizations count
      if (orgsRes.ok) {
        const orgsData = await orgsRes.json();
        newCounts.organizations = orgsData.pagination?.total || orgsData.total || 0;
      }

      // Parse orders count and split by type
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        const allOrders = ordersData.data || [];
        // We need to fetch all orders to count by type, but we can optimize this later with a backend endpoint
        const adOrdersCount = allOrders.filter(o => o.orderType !== 'stationery').length;
        const stationeryOrdersCount = allOrders.filter(o => o.orderType === 'stationery').length;
        newCounts.adOrders = adOrdersCount;
        newCounts.stationeryOrders = stationeryOrdersCount;
      }

      // Parse stationery enquiries count
      if (stationeryEnquiriesRes.ok) {
        const enquiriesData = await stationeryEnquiriesRes.json();
        newCounts.stationeryEnquiries = enquiriesData.pagination?.total || enquiriesData.total || (enquiriesData.data?.length || 0);
      }

      // Ad enquiries placeholder
      newCounts.adEnquiries = 0;

      setCounts(newCounts);
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  }, [getToken]);

  // Fetch archived offers
  const fetchArchivedOffers = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const response = await fetch(`${API_URL}/offers?archived=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setArchivedOffers(data.data || []);
        // Update count
        setCounts(prev => ({ ...prev, offers: data.pagination?.total || data.total || (data.data?.length || 0) }));
      }
    } catch (error) {
      console.error('Error fetching archived offers:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load archived offers',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  // Fetch archived organizations
  const fetchArchivedOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const response = await fetch(`${API_URL}/organizations?archived=true&limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Handle both old format (array) and new format (object with data property)
        if (result.success && result.data) {
          setArchivedOrganizations(result.data);
          setCounts(prev => ({ ...prev, organizations: result.pagination?.total || result.total || result.data.length }));
        } else if (Array.isArray(result)) {
          setArchivedOrganizations(result);
          setCounts(prev => ({ ...prev, organizations: result.length }));
        } else {
          setArchivedOrganizations([]);
          setCounts(prev => ({ ...prev, organizations: 0 }));
        }
      }
    } catch (error) {
      console.error('Error fetching archived organizations:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load archived organizations',
        severity: 'error'
      });
      setArchivedOrganizations([]);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  // Fetch archived ad orders
  const fetchArchivedAdOrders = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const response = await fetch(`${API_URL}/orders/archived`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter for ad orders only (not stationery)
        const adOrders = (data.data || []).filter(order => order.orderType !== 'stationery');
        setArchivedAdOrders(adOrders);
        setCounts(prev => ({ ...prev, adOrders: adOrders.length }));
      }
    } catch (error) {
      console.error('Error fetching archived ad orders:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load archived ad orders',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  // Fetch archived ad enquiries (placeholder - will need backend implementation)
  const fetchArchivedAdEnquiries = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      // TODO: Implement ad enquiries endpoint when available
      // For now, set empty array
      setArchivedAdEnquiries([]);
    } catch (error) {
      console.error('Error fetching archived ad enquiries:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load archived ad enquiries',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  // Fetch archived stationery orders
  const fetchArchivedStationeryOrders = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const response = await fetch(`${API_URL}/orders/archived`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter for stationery orders only
        const stationeryOrders = (data.data || []).filter(order => order.orderType === 'stationery');
        setArchivedStationeryOrders(stationeryOrders);
        setCounts(prev => ({ ...prev, stationeryOrders: stationeryOrders.length }));
      }
    } catch (error) {
      console.error('Error fetching archived stationery orders:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load archived stationery orders',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  // Fetch archived stationery enquiries (converted ones)
  const fetchArchivedStationeryEnquiries = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const response = await fetch(`${API_URL}/stationery-enquiries?status=converted`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setArchivedStationeryEnquiries(data.data || []);
        setCounts(prev => ({ ...prev, stationeryEnquiries: data.pagination?.total || data.total || (data.data?.length || 0) }));
      }
    } catch (error) {
      console.error('Error fetching archived stationery enquiries:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load archived stationery enquiries',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  // Fetch all school enquiries (all statuses) for archive
  const fetchArchivedSchoolEnquiries = useCallback(async () => {
    setSchoolEnquiriesLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/school-enquiries?archived=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const rows = data.data || [];
        setArchivedSchoolEnquiries(rows);
        setCounts(prev => ({ ...prev, schoolEnquiries: rows.length }));
      }
    } catch (error) {
      console.error('Error fetching school enquiries archive:', error);
      setSnackbar({ open: true, message: 'Failed to load school enquiries', severity: 'error' });
    } finally {
      setSchoolEnquiriesLoading(false);
    }
  }, [getToken]);

  // Fetch counts on initial load
  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Fetch data based on active tab
  useEffect(() => {
    if (tabValue === 0) {
      fetchArchivedOffers();
    } else if (tabValue === 1) {
      fetchArchivedOrganizations();
    } else if (tabValue === 2) {
      fetchArchivedAdOrders();
    } else if (tabValue === 3) {
      fetchArchivedAdEnquiries();
    } else if (tabValue === 4) {
      fetchArchivedStationeryOrders();
    } else if (tabValue === 5) {
      fetchArchivedStationeryEnquiries();
    } else if (tabValue === 6) {
      fetchArchivedSchoolEnquiries();
    }
  }, [tabValue, fetchArchivedOffers, fetchArchivedOrganizations, fetchArchivedAdOrders, fetchArchivedAdEnquiries, fetchArchivedStationeryOrders, fetchArchivedStationeryEnquiries, fetchArchivedSchoolEnquiries]);

  const handleUnarchive = async () => {
    const { item, type } = unarchiveDialog;
    if (!item || !type) return;

    try {
      const token = await getToken();
      let endpoint;
      let updatePayload = null;
      
      if (type === 'offer') {
        endpoint = `${API_URL}/offers/${item.id}/unarchive`;
      } else if (type === 'organization') {
        endpoint = `${API_URL}/organizations/${item.id}/unarchive`;
      } else if (type === 'order') {
        endpoint = `${API_URL}/orders/${item.id}/unarchive`;
      } else if (type === 'stationery-enquiry') {
        // For stationery enquiries, we update the status back to a non-converted status
        endpoint = `${API_URL}/stationery-enquiries/${item.id}`;
        updatePayload = { 
          status: 'contacted', // Change from 'converted' back to 'contacted'
          convertedToOrderId: null // Clear the order link
        };
      }
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: updatePayload ? JSON.stringify(updatePayload) : undefined
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: `${type === 'stationery-enquiry' ? 'Stationery enquiry' : type.charAt(0).toUpperCase() + type.slice(1)} unarchived successfully`,
          severity: 'success'
        });
        
        // Refresh the appropriate list
        if (type === 'offer') {
          await fetchArchivedOffers();
        } else if (type === 'organization') {
          await fetchArchivedOrganizations();
        } else if (type === 'order') {
          // Refresh both ad and stationery orders
          await fetchArchivedAdOrders();
          await fetchArchivedStationeryOrders();
        } else if (type === 'stationery-enquiry') {
          await fetchArchivedStationeryEnquiries();
        }
        
        // Refresh counts
        await fetchCounts();
      } else {
        throw new Error('Failed to unarchive');
      }
    } catch (error) {
      console.error('Error unarchiving:', error);
      setSnackbar({
        open: true,
        message: `Failed to unarchive ${type === 'stationery-enquiry' ? 'stationery enquiry' : type}`,
        severity: 'error'
      });
    } finally {
      setUnarchiveDialog({ open: false, item: null, type: null });
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Ordered': 'success',
      'Offered': 'info',
      'Not Interested': 'error',
      'Expired': 'default',
      'Cancelled': 'error'
    };
    return statusColors[status] || 'default';
  };

  // Filter offers
  const filteredOffers = archivedOffers.filter(offer => {
    if (!searchTerm) return true;
    return (
      offer.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      offer.id?.toString().includes(searchTerm)
    );
  });

  // Filter organizations
  const filteredOrganizations = Array.isArray(archivedOrganizations) 
    ? archivedOrganizations.filter(org => {
        if (!searchTerm) return true;
        return (
          org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          org.id?.toString().includes(searchTerm) ||
          org.primaryName?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      })
    : [];

  // Filter ad orders
  const filteredAdOrders = archivedAdOrders.filter(order => {
    if (!searchTerm) return true;
    return (
      order.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.organizationName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.schoolName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id?.toString().includes(searchTerm)
    );
  });

  // Filter ad enquiries
  const filteredAdEnquiries = archivedAdEnquiries.filter(enquiry => {
    if (!searchTerm) return true;
    return (
      enquiry.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.emailAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.id?.toString().includes(searchTerm)
    );
  });

  // Filter stationery orders
  const filteredStationeryOrders = archivedStationeryOrders.filter(order => {
    if (!searchTerm) return true;
    return (
      order.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.organizationName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id?.toString().includes(searchTerm)
    );
  });

  // Filter stationery enquiries
  const filteredStationeryEnquiries = archivedStationeryEnquiries.filter(enquiry => {
    if (!searchTerm) return true;
    return (
      enquiry.schoolOrBusinessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.emailAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.id?.toString().includes(searchTerm)
    );
  });

  // Filter school enquiries
  const filteredSchoolEnquiries = archivedSchoolEnquiries.filter(enq => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (
      enq.school_name?.toLowerCase().includes(t) ||
      enq.contact_person?.toLowerCase().includes(t) ||
      enq.email?.toLowerCase().includes(t) ||
      enq.products?.toLowerCase().includes(t) ||
      enq.id?.toString().includes(t)
    );
  });

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 2 }}>
      <PageHeader 
        title="Archive"
        subtitle="View and manage archived offers and organizations"
      />

      <Paper sx={{ width: '100%', mb: 2 }}>
        {/* Tabs */}
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => {
            setTabValue(newValue);
            localStorage.setItem('archiveActiveTab', newValue.toString());
            setSearchTerm(''); // Clear search when switching tabs
          }}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<DescriptionIcon />} 
            iconPosition="start" 
            label={`Offers (${searchTerm ? filteredOffers.length : counts.offers})`} 
          />
          <Tab 
            icon={<BusinessIcon />} 
            iconPosition="start" 
            label={`Organizations (${searchTerm ? filteredOrganizations.length : counts.organizations})`} 
          />
          <Tab 
            icon={<DescriptionIcon />} 
            iconPosition="start" 
            label={`Ad Orders (${searchTerm ? filteredAdOrders.length : counts.adOrders})`} 
          />
          <Tab 
            icon={<ContactMailIcon />} 
            iconPosition="start" 
            label={`Ad Enquiries (${searchTerm ? filteredAdEnquiries.length : counts.adEnquiries})`} 
          />
          <Tab 
            icon={<DescriptionIcon />} 
            iconPosition="start" 
            label={`Stationery Orders (${searchTerm ? filteredStationeryOrders.length : counts.stationeryOrders})`} 
          />
          <Tab 
            icon={<ContactMailIcon />} 
            iconPosition="start" 
            label={`Stationery Enquiries (${searchTerm ? filteredStationeryEnquiries.length : counts.stationeryEnquiries})`} 
          />
          <Tab 
            icon={<ContactMailIcon />} 
            iconPosition="start" 
            label={`School Enquiries (${searchTerm ? filteredSchoolEnquiries.length : counts.schoolEnquiries})`} 
          />
        </Tabs>

        {/* Search Bar */}
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder={
              tabValue === 0 
                ? "Search by business name or offer ID..." 
                : tabValue === 1
                ? "Search by organization name, ID, or contact..."
                : tabValue === 2
                ? "Search by ad order ID, business name, or school..."
                : tabValue === 3
                ? "Search by ad enquiry ID, name, or email..."
                : tabValue === 4
                ? "Search by stationery order ID or organization..."
                : tabValue === 5
                ? "Search by stationery enquiry ID, name, organization, or email..."
                : "Search by school name, contact, email, or products..."
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Content based on active tab */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : tabValue === 0 ? (
          /* Offers Table */
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Offer ID</TableCell>
                  <TableCell>Business Name</TableCell>
                  <TableCell>School</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total Price</TableCell>
                  <TableCell>Sales Person</TableCell>
                  <TableCell>Offer Date</TableCell>
                  <TableCell>Archived Date</TableCell>
                  <TableCell>Archived By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOffers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No archived offers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOffers.map((offer) => (
                    <TableRow key={offer.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          #{offer.id}
                        </Typography>
                      </TableCell>
                      <TableCell>{offer.businessName}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="info.main" fontWeight={500}>
                          {offer.schoolName || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={offer.status} 
                          color={getStatusColor(offer.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          ${parseFloat(offer.totalPrice || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>{offer.salesPersonName || 'N/A'}</TableCell>
                      <TableCell>{formatDate(offer.offerDate)}</TableCell>
                      <TableCell>{formatDate(offer.archivedAt)}</TableCell>
                      <TableCell>{offer.archivedByName || 'Unknown'}</TableCell>
                      <TableCell>
                        <Tooltip title="Unarchive">
                          <IconButton
                            size="small"
                            onClick={() => setUnarchiveDialog({ open: true, item: offer, type: 'offer' })}
                          >
                            <UnarchiveIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : tabValue === 1 ? (
          /* Organisations Table */
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Organisation ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Primary Contact</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Archived Date</TableCell>
                  <TableCell>Archived By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrganizations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No archived organizations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrganizations.map((org) => (
                    <TableRow key={org.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          #{org.id}
                        </Typography>
                      </TableCell>
                      <TableCell>{org.name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={org.isSchool ? 'School' : org.isBusiness ? 'Business' : 'Other'} 
                          color={org.isSchool ? 'primary' : org.isBusiness ? 'secondary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{org.primaryName || 'N/A'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {org.primaryEmail || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {[org.suburb, org.state].filter(Boolean).join(', ') || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(org.archived_at)}</TableCell>
                      <TableCell>{org.archived_by_name || 'Unknown'}</TableCell>
                      <TableCell>
                        <Tooltip title="Unarchive">
                          <IconButton
                            size="small"
                            onClick={() => setUnarchiveDialog({ open: true, item: org, type: 'organization' })}
                          >
                            <UnarchiveIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : tabValue === 2 ? (
          /* Ad Orders Table */
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Order Type</TableCell>
                  <TableCell>Organisation</TableCell>
                  <TableCell>School</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total Price</TableCell>
                  <TableCell>Sales Person</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Archived Date</TableCell>
                  <TableCell>Archived By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAdOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No archived ad orders found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAdOrders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          #{order.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={order.orderType || 'N/A'} 
                          color={order.orderType === 'ad' ? 'primary' : order.orderType === 'school' ? 'secondary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.organizationName || order.businessName || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="info.main" fontWeight={500}>
                          {order.schoolName || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={order.status || 'N/A'} 
                          color={getStatusColor(order.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          ${parseFloat(order.totalPrice || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.salesPersonName || 'N/A'}</TableCell>
                      <TableCell>{formatDate(order.createdAt)}</TableCell>
                      <TableCell>{formatDate(order.archived_at)}</TableCell>
                      <TableCell>{order.archivedByName || 'Unknown'}</TableCell>
                      <TableCell>
                        <Tooltip title="Unarchive">
                          <IconButton
                            size="small"
                            onClick={() => setUnarchiveDialog({ open: true, item: order, type: 'order' })}
                          >
                            <UnarchiveIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : tabValue === 3 ? (
          /* Ad Enquiries Table */
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Organisation</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Archived Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAdEnquiries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        {loading ? 'Loading...' : 'No archived ad enquiries found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAdEnquiries.map((enquiry) => (
                    <TableRow key={enquiry.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          #{enquiry.id}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(enquiry.createdAt)}</TableCell>
                      <TableCell>{`${enquiry.firstName} ${enquiry.lastName}`}</TableCell>
                      <TableCell>{enquiry.businessName}</TableCell>
                      <TableCell>{enquiry.emailAddress}</TableCell>
                      <TableCell>{enquiry.phoneNumber}</TableCell>
                      <TableCell>
                        <Chip 
                          label={enquiry.status} 
                          size="small" 
                          color="default" 
                        />
                      </TableCell>
                      <TableCell>{formatDate(enquiry.updatedAt)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : tabValue === 4 ? (
          /* Stationery Orders Table */
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Organisation</TableCell>
                  <TableCell>Product Type</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total Price</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Archived Date</TableCell>
                  <TableCell>Archived By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStationeryOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No archived stationery orders found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStationeryOrders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          #{order.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.organizationName || order.businessName || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.productType || 'N/A'}</TableCell>
                      <TableCell>{order.quantity || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={order.status || 'N/A'} 
                          color={getStatusColor(order.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          ${parseFloat(order.totalPrice || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(order.createdAt)}</TableCell>
                      <TableCell>{formatDate(order.archived_at)}</TableCell>
                      <TableCell>{order.archivedByName || 'Unknown'}</TableCell>
                      <TableCell>
                        <Tooltip title="Unarchive">
                          <IconButton
                            size="small"
                            onClick={() => setUnarchiveDialog({ open: true, item: order, type: 'order' })}
                          >
                            <UnarchiveIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}

        {/* Stationery Enquiries Tab */}
        {tabValue === 5 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Organisation</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Converted To Order</TableCell>
                  <TableCell>Converted Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStationeryEnquiries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        {loading ? 'Loading...' : 'No archived stationery enquiries found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStationeryEnquiries.map((enquiry) => (
                    <TableRow key={enquiry.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          #{enquiry.id}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(enquiry.createdAt)}</TableCell>
                      <TableCell>{`${enquiry.firstName} ${enquiry.lastName}`}</TableCell>
                      <TableCell>{enquiry.schoolOrBusinessName}</TableCell>
                      <TableCell>{enquiry.emailAddress}</TableCell>
                      <TableCell>{enquiry.phoneNumber}</TableCell>
                      <TableCell>
                        <Tooltip title={enquiry.productDescription}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {enquiry.productDescription}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {enquiry.convertedToOrderId ? (
                          <Chip 
                            label={`Order #${enquiry.convertedToOrderId}`} 
                            size="small" 
                            color="success" 
                          />
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>{formatDate(enquiry.updatedAt)}</TableCell>
                      <TableCell>
                        <Tooltip title="Unarchive">
                          <IconButton
                            size="small"
                            onClick={() => setUnarchiveDialog({ open: true, item: enquiry, type: 'stationery-enquiry' })}
                          >
                            <UnarchiveIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {/* School Enquiries Tab */}
        {tabValue === 6 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>School Name</TableCell>
                  <TableCell>Products</TableCell>
                  <TableCell>Contact Person</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Demo Date</TableCell>
                  <TableCell>Added</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schoolEnquiriesLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : filteredSchoolEnquiries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary">No school enquiries found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredSchoolEnquiries.map((enq) => {
                  const statusColors = {
                    New: 'primary', Contacted: 'info', Demo: 'warning',
                    Ordered: 'success', Cancelled: 'default'
                  };
                  return (
                    <TableRow key={enq.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{enq.school_name}</Typography>
                      </TableCell>
                      <TableCell>{enq.products || '—'}</TableCell>
                      <TableCell>{enq.contact_person || '—'}</TableCell>
                      <TableCell>{enq.phone || '—'}</TableCell>
                      <TableCell>{enq.email || '—'}</TableCell>
                      <TableCell>
                        <Chip label={enq.status} color={statusColors[enq.status] || 'default'} size="small" />
                      </TableCell>
                      <TableCell>{enq.demo_date ? formatDate(enq.demo_date) : '—'}</TableCell>
                      <TableCell>{formatDate(enq.created_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Unarchive Confirmation Dialog */}
      <Dialog
        open={unarchiveDialog.open}
        onClose={() => setUnarchiveDialog({ open: false, item: null, type: null })}
      >
        <DialogTitle>
          Unarchive {
            unarchiveDialog.type === 'offer' ? 'Offer' : 
            unarchiveDialog.type === 'organization' ? 'Organisation' : 
            unarchiveDialog.type === 'stationery-enquiry' ? 'Stationery Enquiry' :
            'Order'
          }
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to unarchive this {
              unarchiveDialog.type === 'stationery-enquiry' ? 'stationery enquiry' : unarchiveDialog.type
            }? 
            {unarchiveDialog.type === 'stationery-enquiry' ? (
              <> It will be restored to the active enquiries list and its link to the order will be removed.</>
            ) : (
              <> It will be restored to the active {unarchiveDialog.type}s list.</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnarchiveDialog({ open: false, item: null, type: null })}>
            Cancel
          </Button>
          <Button onClick={handleUnarchive} variant="contained" color="primary">
            Unarchive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Archive;
